import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ApiRequestError } from "../../api";
import { useAuth } from "../../auth/store";
import { AutoLoadMore, PageHeader, RowMenu, useToast } from "../../components/ui";
import { EmptyBellMark, EmptySearchMark, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { countOf, formatDateTime, formatRelative } from "../../lib/format";
import { SOURCES, notificationsApi, notifyKeys, type InboxItem, type InboxQuery, type NotificationRow } from "./api";

type NotificationPatch = Parameters<typeof notificationsApi.patchNotification>[2];

function useOrgId(): string | undefined {
  return useAuth().currentOrgId;
}

function sourceLabel(source: string): string {
  if (source === "notification.test") return "Test message";
  if (source === "channel.disabled") return "Webhook";
  return SOURCES.find((s) => s.id === source)?.label ?? source;
}

const STATE_LABELS: Record<string, string> = { open: "Open", acknowledged: "Acknowledged", snoozed: "Snoozed", resolved: "Resolved" };
const stateLabel = (s: string) => STATE_LABELS[s] ?? s;

const SOURCE_OPTIONS = [
  ...SOURCES.filter((s) => !s.legacy).map((s) => ({ id: s.id as string, label: s.label })),
  { id: "channel.disabled", label: "Webhook" },
  { id: "notification.test", label: "Test message" },
];

/** Shared mutations for the list and the detail page. */
function useInboxActions() {
  const orgId = useOrgId() ?? "";
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () => void qc.invalidateQueries({ queryKey: notifyKeys.inbox(orgId) });
  const ack = useMutation({
    mutationFn: (id: string) => notificationsApi.ackInbox(orgId, id),
    onSuccess: refresh,
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const act = useMutation({
    mutationFn: ({ id, body }: { id: string; body: NotificationPatch }) => notificationsApi.patchNotification(orgId, id, body),
    onSuccess: refresh,
    onError: (e) => toast.push("error", (e as Error).message),
  });
  return { orgId, ack, act };
}

function ActionsMenu({ item, onAck, onAction, label }: { item: InboxItem; onAck: () => void; onAction: (body: NotificationPatch) => void; label: string }) {
  const n = item.notification;
  return (
    <RowMenu label={label} testId={`inbox-menu-${item.id}`}>
      {!item.ackedAt && <button role="menuitem" onClick={onAck}>Mark read</button>}
      {n.state === "open" && <button role="menuitem" onClick={() => onAction({ assignee: "me" })}>Assign to me</button>}
      {n.state !== "resolved" && (
        <>
          <button role="menuitem" onClick={() => onAction({ snooze: "1h" })}>Snooze 1 hour</button>
          <button role="menuitem" onClick={() => onAction({ snooze: "24h" })}>Snooze 24 hours</button>
          <button role="menuitem" onClick={() => onAction({ snooze: "release" })}>Snooze until next release</button>
          <button role="menuitem" onClick={() => onAction({ state: "resolved" })}>Resolve</button>
        </>
      )}
      {n.state === "resolved" && <button role="menuitem" onClick={() => onAction({ state: "open" })}>Reopen</button>}
    </RowMenu>
  );
}

export function InboxPage() {
  const { orgId, ack, act } = useInboxActions();
  const [qInput, setQInput] = useState("");
  const [filters, setFilters] = useState<InboxQuery>({});
  useEffect(() => {
    const timer = window.setTimeout(() => setFilters((f) => ({ ...f, q: qInput.trim() || undefined })), 250);
    return () => window.clearTimeout(timer);
  }, [qInput]);
  const inbox = useInfiniteQuery({
    queryKey: notifyKeys.inboxList(orgId, filters),
    queryFn: ({ pageParam }) => notificationsApi.inbox(orgId, { ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!orgId,
    refetchInterval: 30_000,
  });
  const items = useMemo(() => inbox.data?.pages.flatMap((p) => p.items) ?? [], [inbox.data]);
  const unread = inbox.data?.pages[0]?.unread;
  const filtered = !!(filters.q || filters.state || filters.source || filters.unread);
  const clear = () => { setQInput(""); setFilters({}); };

  return (
    <div className="page">
      <PageHeader title="Inbox" subtitle={unread !== undefined ? `${unread} unread` : undefined} />
      {!orgId && <div className="card">Select an organization to see its inbox.</div>}
      {orgId && (
        <div className="inbox-filters" role="search" aria-label="Inbox filters">
          <input
            className="input inbox-search"
            type="search"
            placeholder="Search messages"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            aria-label="Search messages"
            autoComplete="off"
          />
          <button
            type="button"
            className="btn"
            aria-pressed={!!filters.unread}
            onClick={() => setFilters((f) => ({ ...f, unread: f.unread ? undefined : true }))}
          >
            Unread
          </button>
          <select className="input" value={filters.state ?? ""} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value || undefined }))} aria-label="Status" autoComplete="off">
            <option value="">Any status</option>
            {Object.entries(STATE_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select className="input" value={filters.source ?? ""} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value || undefined }))} aria-label="Source" autoComplete="off">
            <option value="">Any source</option>
            {SOURCE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      )}
      {inbox.isError && <div className="card"><ErrorState error={inbox.error} retry={() => void inbox.refetch()} /></div>}
      {inbox.isSuccess && items.length === 0 && (
        <div className="card">
          {filtered ? (
            <EmptyState mark={<EmptySearchMark />} title="No messages match" action={<button className="btn" onClick={clear}>Clear filters</button>} />
          ) : (
            <EmptyState mark={<EmptyBellMark />} title="You're all caught up" />
          )}
        </div>
      )}
      <ul className="inbox-list" aria-label="Inbox" aria-busy={inbox.isPending || inbox.isFetchingNextPage}>
        {items.map((it) => (
          <InboxRow key={it.id} item={it} onAck={() => ack.mutate(it.id)} onAction={(body) => act.mutate({ id: it.notification.id, body })} />
        ))}
        {orgId && inbox.isPending && <InboxSkeletonRows count={5} />}
        {inbox.isFetchingNextPage && <InboxSkeletonRows count={3} />}
      </ul>
      <AutoLoadMore hasMore={!!inbox.hasNextPage} loading={inbox.isFetchingNextPage} loadMore={() => void inbox.fetchNextPage()} label="Loading older messages" />
    </div>
  );
}

function InboxSkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <li key={`sk-${i}`} className="inbox-row inbox-row-skeleton" aria-hidden="true">
          <span className="mark skeleton" />
          <div className="inbox-main">
            <span className="skeleton">Loading title</span>
            <span className="skeleton">Loading summary</span>
            <span className="skeleton">Loading details</span>
          </div>
          <span />
        </li>
      ))}
    </>
  );
}

function InboxRow({ item, onAck, onAction }: { item: InboxItem; onAck: () => void; onAction: (body: NotificationPatch) => void }) {
  const n = item.notification;
  const navigate = useNavigate();
  const open = () => void navigate({ to: "/inbox/$itemId" as never, params: { itemId: item.id } as never });
  return (
    <li
      className={`inbox-row ${item.ackedAt ? "acked" : "unread"}`}
      data-testid="inbox-item"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button, .row-menu")) return;
        open();
      }}
    >
      <span className={`mark mark-${n.state || "open"}`} title={stateLabel(n.state)} aria-label={stateLabel(n.state)} />
      <div className="inbox-main">
        <div className="inbox-title">
          <Link to={"/inbox/$itemId" as never} params={{ itemId: item.id } as never} className="inbox-link">{n.title}</Link>
          {n.state !== "open" && <span className="badge neutral">{stateLabel(n.state)}{n.assignee ? ` · ${n.assignee}` : ""}</span>}
        </div>
        {n.summary && <div className="small muted inbox-summary">{n.summary}</div>}
        <div className="small muted">
          {sourceLabel(n.source)}
          {n.counts ? ` · ${countOf(n.counts.total, "occurrence")}, ${countOf(n.counts.users, "user")}` : ""}
          {n.route ? ` · ${n.route}` : ""}
          {" · "}
          {formatRelative(item.createdAt)}
        </div>
      </div>
      <div className="inbox-side">
        {!item.ackedAt && <span className="unread-dot" aria-label="Unread" />}
        <ActionsMenu item={item} onAck={onAck} onAction={onAction} label={`Actions for ${n.title}`} />
      </div>
    </li>
  );
}

function sameOrigin(href: string): boolean {
  try {
    return new URL(href, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function ExternalOrLocal({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return sameOrigin(href) ? <a href={href} className={className}>{children}</a> : <a href={href} className={className} target="_blank" rel="noreferrer">{children}</a>;
}

function linkList(n: NotificationRow): Array<{ label: string; href: string }> {
  const out: Array<{ label: string; href: string }> = [];
  if (n.links.replay) out.push({ label: "Watch replay", href: n.links.replay });
  if (n.links.cluster) out.push({ label: "Open issue", href: n.links.cluster });
  if (n.links.error) out.push({ label: "Open error", href: n.links.error });
  if (n.links.trace) out.push({ label: "Open trace", href: n.links.trace });
  if (n.source === "notification.test") return out;
  if (n.source === "channel.disabled" && n.links.dashboard) {
    out.push({ label: "Review webhook", href: n.links.dashboard });
    return out;
  }
  if (n.links.dashboard && out.length === 0) out.push({ label: "Open in dashboard", href: n.links.dashboard });
  return out;
}

export function InboxItemPage() {
  const { itemId } = useParams({ strict: false }) as { itemId: string };
  const { orgId, ack, act } = useInboxActions();
  const inbox = useQuery({ queryKey: notifyKeys.inboxItem(orgId, itemId), queryFn: () => notificationsApi.inboxItem(orgId, itemId), enabled: !!orgId, retry: (n, e) => !(e instanceof ApiRequestError && e.status === 404) && n < 2 });
  const item = inbox.data;
  const back = <Link to={"/inbox" as never} className="btn ghost">← Inbox</Link>;

  // Opening a notification reads it.
  const acked = useRef(false);
  useEffect(() => {
    if (item && !item.ackedAt && !acked.current) {
      acked.current = true;
      ack.mutate(item.id);
    }
  }, [item, ack]);

  if (inbox.isPending) return <div className="page"><PageHeader back={back} title="Notification" /><Skeleton lines={6} /></div>;
  const missing = inbox.error instanceof ApiRequestError && inbox.error.status === 404;
  if (inbox.isError && !missing) return <div className="page"><PageHeader back={back} title="Notification" /><div className="card"><ErrorState error={inbox.error} retry={() => void inbox.refetch()} /></div></div>;
  if (!item) {
    return (
      <div className="page">
        <PageHeader back={back} title="Notification" />
        <div className="card"><EmptyState mark={<EmptyBellMark />} title="This notification is no longer in your inbox" /></div>
      </div>
    );
  }

  const n = item.notification;
  const links = linkList(n);
  const [primary, ...rest] = links;
  const onAction = (body: NotificationPatch) => act.mutate({ id: n.id, body });

  return (
    <div className="page">
      <PageHeader
        back={back}
        title={n.title}
        subtitle={
          <span className="inbox-detail-badges">
            <span className={`badge sev-badge sev-badge-${n.severity}`}>{n.severity}</span>
            <span className={`badge ${n.state === "open" ? "warn" : "neutral"}`}>{stateLabel(n.state)}</span>
            <span className="muted">{sourceLabel(n.source)} · {formatRelative(item.createdAt)}</span>
          </span>
        }
        actions={
          <>
            {primary && <ExternalOrLocal href={primary.href} className="btn primary">{primary.label}</ExternalOrLocal>}
            <ActionsMenu item={item} onAck={() => ack.mutate(item.id)} onAction={onAction} label="Notification actions" />
          </>
        }
      />

      {n.summary && <div className="card inbox-detail-summary">{n.summary}</div>}

      <div className="card">
        <dl className="kv inbox-kv">
          <dt>Source</dt><dd>{sourceLabel(n.source)}</dd>
          <dt>Severity</dt><dd className="cap">{n.severity}</dd>
          <dt>Status</dt><dd>{stateLabel(n.state)}{n.snoozedUntil ? ` until ${formatDateTime(n.snoozedUntil)}` : ""}</dd>
          {n.assignee && (<><dt>Assignee</dt><dd>{n.assignee}</dd></>)}
          {n.counts && (<><dt>Occurrences</dt><dd>{countOf(n.counts.total, "total", "total")} · {n.counts.last1h} in the last hour · {countOf(n.counts.users, "user")}</dd></>)}
          {n.route && (<><dt>Route</dt><dd className="mono">{n.route}</dd></>)}
          {n.release && (<><dt>Release</dt><dd className="mono">{n.release}</dd></>)}
          {n.firstSeenAt && (<><dt>First seen</dt><dd>{formatDateTime(n.firstSeenAt)}</dd></>)}
          {n.lastSeenAt && (<><dt>Last seen</dt><dd>{formatDateTime(n.lastSeenAt)}</dd></>)}
          <dt>Received</dt><dd>{formatDateTime(item.createdAt)}</dd>
          {rest.length > 0 && (
            <>
              <dt>Links</dt>
              <dd className="inbox-detail-links">{rest.map((l) => <ExternalOrLocal key={l.href} href={l.href}>{l.label}</ExternalOrLocal>)}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
